import React from "react";
import {useSubscription} from "../../subscription/useSubscription";
import {useSelector} from "react-redux";
import {fractionToPercent, numberToFormattedPercent} from "../../performance/PercentageChange";
import {Clock, LocalDate} from "js-joda";
import ApexTimeseries from "./ApexTimeseries";
import {logAction} from "../../../logging/logEvent";
import {useDebounced} from "../../../hooks/Debounced";

const translateTimeseries = (timeseries) => {
    if (!timeseries) {
        return [];
    }
    return timeseries.map(stockReturn => ({
        x: stockReturn.date,
        y: fractionToPercent(stockReturn.returnPercent),
        returnPercent: stockReturn.returnPercent,
        price: stockReturn.price
    }));
}

const StockChart = ({stockDetails}) => {
    const clock = useSelector(({clock}) => clock || Clock.systemDefaultZone());
    const today = LocalDate.now(clock);
    const oneYearAgo = today.minusYears(1);
    const dailyReturnsUrl = `/TradeIdeasMonitor/ui/api/dailyReturns/${stockDetails.stockId}/${oneYearAgo}/${today}`;
    useSubscription("DAILY_RETURNS", dailyReturnsUrl, 60 * 60);
    const {stockChart: {benchmarkTimeseries, stockTimeseries}} = useSelector(({stockChart}) => ({stockChart}));
    const logScrolled = useDebounced(() => {
        logAction({
            type: "STOCK_CHART_SCROLLED",
            stockName: stockDetails.name,
        });
    }, 2000, []);

    if (!stockTimeseries) {
        return null;
    }
    if (!stockTimeseries.timeseries || stockTimeseries.timeseries.length === 0) {
        return null;
    }

    const series = [{
        name: stockDetails.name,
        data: translateTimeseries(stockTimeseries.timeseries)
    }];
    if (benchmarkTimeseries && benchmarkTimeseries.timeseries) {
        series.push({
            name: benchmarkTimeseries.name,
            data: translateTimeseries(benchmarkTimeseries.timeseries)
        });
    }

    const stockLineColour = '#458535';
    const benchmarkLineColour = '#bdb9b4';
    const stockChartOptions = {
        title: {
            text: 'Daily Returns',
        },
        colors: [
            stockLineColour,
            benchmarkLineColour
        ],
        chart: {
            events: {
                legendClick: function (chartContext, seriesIndex, config) {
                    const seriesDetails = series[seriesIndex];
                    if (seriesDetails) {
                        const seriesName = seriesDetails.name;
                        logAction({
                            type: "STOCK_CHART_LEGEND_CLICKED",
                            stockName: seriesName
                        });
                    }
                },
                zoomed: function (chartContext, {xaxis, yaxis}) {
                    logAction({
                        type: "STOCK_CHART_ZOOMED",
                        stockName: stockDetails.name
                    });
                },
                scrolled: function (chartContext, {xaxis}) {
                    logScrolled();
                }
            },
        },
        tooltip: {
            theme: 'dark',
            y: {
                formatter: (value, {seriesIndex, dataPointIndex}) => {
                    if (value === undefined || value === null) {
                        return "Unknown";
                    }
                    const seriesData = series[seriesIndex].data;
                    const dataPoint = seriesData[dataPointIndex];
                    const price = dataPoint.price;
                    const percentage = numberToFormattedPercent(dataPoint.returnPercent);
                    return `${price} (${percentage})`;
                }
            }
        },
        yaxis: {
            labels: {
                formatter: function (val) {
                    return `${val.toFixed(2)}%`;
                },
            },
        },
    };

    return <div className="t-stock-chart-container">
        <ApexTimeseries options={stockChartOptions}
                        series={series}
                        height={360}/>
    </div>;
};

export default StockChart;